import {SchoolList} from "../components/school/list";
import {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {NavLink, useNavigate} from 'react-router-dom';
import {Blockquote, Breadcrumb, BreadcrumbHome, BreadcrumbItem, Button, Modal, Text} from "@wfp/ui";
import {iconAddGlyph, iconDeleteGlyph, iconUpload, iconDownload} from '@wfp/icons';
import {ListPage, School, Role} from "../models/types";
import {SchoolError, validateSchool} from "../models/validate-school";
import {deleteSchool, getSchools, saveSchool, downloadSchools} from "../restapi/school";
import {useGetSchools} from "../hooks/school";
import {useLoggedUser} from "../hooks/user";
import {LoadingCenter} from "../components/commons/loading-center";
import {SMETSTypeahead} from "../components/commons/typeahead/typeahead";
import {SchoolForm} from "../components/school/form";
import {SchoolBulkUploadModal} from "../components/school/bulk-upload-modal";

export default function Schools() {
    const {i18n, t} = useTranslation();
    const navigate = useNavigate();
    const [page, setPage] = useState<ListPage<School>>();
    const [error, setError] = useState<SchoolError>();
    const [school, setSchool] = useState<School>();
    const [selected, setSelected] = useState<School>();
    const [openCreate, setOpenCreate] = useState(false);
    const [openDisable, setOpenDisable] = useState(false);
    const [openBulkUpload, setOpenBulkUpload] = useState(false);
    const data = useGetSchools({onError: (e) => setError(e as SchoolError)});
    const user = useLoggedUser({onError: (e) => {}});

    useEffect(() => {
        if (!!data) {
            setPage(data);
        }
    }, [data]);

    function doReloadPage() {
        getSchools(page?.pageNumber, page?.pageSize, page?.filtering, page?.sorting)
            .then(res => setPage(res))
            .catch(e => setError(e));
    }

    function handleCreateCancel(): void {
        setOpenCreate(false);
        setSchool(undefined);
        setError(undefined);
    }

    function handleCreateSubmit(): void {
        if (!!school) {
            validateSchool(school)
                .then(value => saveSchool(value)
                    .then(() => {
                        setError(undefined);
                        setSchool(undefined);
                        setOpenCreate(false);
                        doReloadPage();
                    })
                    .catch(e => setError(e)))
                .catch(e => setError(e));
        }
    }

    function handleDisableCancel() {
        setOpenDisable(false);
        setError(undefined);
    }

    function handleDisableSubmit() {
        if (!!selected) {
            deleteSchool(selected.id)
                .then(() => {
                    setError(undefined);
                    setOpenDisable(false);
                })
                .catch(error => setError(error));
        }
    }

    function handleClick(id: string | number): void {
        navigate(`/schools/${id}`);
    }

    function handleSelected(id: number): void {
        const selected = (page?.results || []).find(item => item.id === id);
        setSelected(selected);
    }

    return (
        <>
            {!!error?.detail && (
                <Blockquote kind="error" withIcon>
                    {error.detail.toString()}
                </Blockquote>
            )}
            {!error?.detail && !page && (
                <LoadingCenter/>
            )}
            {!!page && (
                <>
                    <div className="visible md:hidden">
                        <SchoolList page={page} mobile={true}/>
                    </div>
                    <div className="hidden md:block">
                        <div className="m-10">
                            <Breadcrumb className='mb-5'>
                                <BreadcrumbItem>
                                    <NavLink to="/"><BreadcrumbHome/></NavLink>
                                </BreadcrumbItem>
                                <BreadcrumbItem disableLink>
                                    {t('school')}
                                </BreadcrumbItem>
                            </Breadcrumb>
                            <div className='mb-5'>
                                <div className="flex mb-5">
                                    <Text kind='h1'>{t("school_overview")}</Text>
                                </div>
                                <div className={`${i18n.dir() === 'ltr' ? 'ml-auto' : 'mr-auto'}`}>
                                    <div className="flex">
                                        <div className='ml-0 w-1/3 text-left'>
                                            <SMETSTypeahead<School>
                                                placeholder={t('search_name') as string}
                                                noOptionsMessage={t('no_option_value') as string}
                                                onSelectOption={(e) => handleClick(e.id)}
                                                search={(e) => getSchools(1, 5, [{
                                                    key: "name_ic",
                                                    value: e
                                                }]).then(res => res.results)}
                                            >
                                            </SMETSTypeahead>
                                        </div>
                                        <div className={`mr-0 ${i18n.dir() === 'ltr' ? 'ml-auto' : 'mr-auto'}`}>
                                            <Button className='!inline-flex' disabled={!selected}
                                                    onClick={() => setOpenDisable(true)} icon={iconDeleteGlyph}
                                                    iconReverse>
                                                {t('delete')}
                                            </Button>
                                        </div>
                                        {user?.role === Role.WFPManager && (
                                            <>
                                                <div className="mx-3">
                                                    <Button onClick={() => downloadSchools()}
                                                            className='!inline-flex' icon={iconDownload} iconReverse>
                                                        {t('download_schools')}
                                                    </Button>
                                                </div>
                                                <div className="mx-3">
                                                    <Button onClick={() => setOpenBulkUpload(true)}
                                                            className='!inline-flex' icon={iconUpload} iconReverse>
                                                        {t('upload_schools')}
                                                    </Button>
                                                </div>
                                            </>
                                        )}
                                        <div className="mx-3">
                                            <Button onClickCapture={() => setOpenCreate(true)} iconDescription="create"
                                                    className='!inline-flex' icon={iconAddGlyph} iconReverse>
                                                {t('create_school')}
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <SchoolList page={page} mobile={false} onClick={handleClick} onSelect={handleSelected} selectCheck={selected?.id}/>
                        </div>
                    </div>
                </>
            )}
            {openCreate && (
                <Modal
                    open={openCreate}
                    modalHeading={t('add_school')!}
                    modalLabel={t('create_school')!}
                    primaryButtonText={t('create')!}
                    primaryButtonDisabled={!school}
                    onRequestSubmit={handleCreateSubmit}
                    onRequestClose={handleCreateCancel}
                    secondaryButtonText={t('cancel')!}
                    onSecondarySubmit={handleCreateCancel}
                >
                    <SchoolForm school={school || {} as School} error={error} onChange={setSchool}/>
                    {!!error?.detail && (
                        <Blockquote
                            kind="error"
                            withIcon>
                            {error.detail}
                        </Blockquote>
                    )}
                </Modal>
            )}
            {openDisable && !!selected && (
                <Modal
                    open={openDisable}
                    modalHeading={t('delete_school')!}
                    modalLabel={t('delete_school')!}
                    primaryButtonText={t('confirm')!}
                    onRequestSubmit={handleDisableSubmit}
                    onRequestClose={handleDisableCancel}
                    secondaryButtonText={t('cancel')!}
                    onSecondarySubmit={handleDisableCancel}
                >
                    <p>{`${t('delete_school_confirm')} ${selected.name}`}</p>
                    {!!error?.detail && (
                        <Blockquote kind="error" withIcon>{error.detail}</Blockquote>
                    )}
                </Modal>
            )}
            {openBulkUpload && (
                <SchoolBulkUploadModal
                    open={openBulkUpload}
                    onClose={() => setOpenBulkUpload(false)}
                    onSuccess={() => {
                        doReloadPage();
                        setOpenBulkUpload(false);
                    }}
                />
            )}
        </>
    );
}
